import React, { useState, useEffect } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import MyPagePanel from '@/components/ui/box/MyPagePanel';
import Paper from '@/components/ui/box/Paper';
import Button from '@/components/ui/buttons/Button';
import ErrorList from '@/components/ui/forms/ErrorList';
import Field from '@/components/ui/forms/Field';
import FormGroup from '@/components/ui/forms/FormGroup';
import Input from '@/components/ui/forms/Input';
import Label from '@/components/ui/forms/Label';
import useReactHookForm from '@/hooks/common/useReactHookForm';
import { SAPURI_EMAIL_REGEXP, HANKAKU_REGEXP } from '@/store/constants';

import styles from './EmailChangeForm.module.scss';

type FormValues = {
  email: string;
};

interface Props {
  onSubmit: (email: string) => void;
  error?: { message: string } | null;
}

const EmailChangeForm: React.FC<Props> = ({ onSubmit, error }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [formValues] = useState<FormValues>({
    email: '',
  });

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(
        intl.formatMessage(
          {
            id: 'forms.errors.required',
          },
          {
            label: intl.formatMessage({
              id: 'mypage.profileEditPage.editEmailModal.labels.email',
            }),
          },
        ),
      )
      .test('email-format', 'メールアドレスの形式が不正です。', (value) => {
        if (!value) return true;
        if (/\.$/.test(value)) return false;
        return SAPURI_EMAIL_REGEXP.test(value);
      })
      .matches(HANKAKU_REGEXP, {
        message: intl.formatMessage(
          {
            id: 'forms.errors.hankaku',
          },
          {
            label: intl.formatMessage({
              id: 'mypage.profileEditPage.editEmailModal.labels.email',
            }),
          },
        ),
        excludeEmptyString: true,
      }),
  });

  const {
    registerWithInnerRef,
    handleSubmit,
    formState: {
      isSubmitting,
      isValid,
      touchedFields,
      errorMessages,
      dirtyFields,
    },
    trigger,
    watch,
    reset,
  } = useReactHookForm<FormValues>({
    validationSchema,
    defaultValues: formValues,
  });

  const emailValue = watch('email');

  useEffect(() => {
    if (emailValue) {
      void trigger('email');
    }
  }, [emailValue, trigger]);

  const handleFormSubmit = (data: FormValues) => {
    onSubmit(data.email);
    reset(data, { keepValues: true });
  };

  const handleCancel = () => {
    navigate('/mypage/profile');
  };

  const inputDisabled = isSubmitting;
  const buttonDisabled = !isValid || isSubmitting;

  return (
    <form
      className={styles.EmailChangeForm}
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit(handleFormSubmit)(e);
      }}
    >
      <Paper>
        <MyPagePanel title="メールアドレスの再設定">
          <FormGroup>
            <Field className={styles.EmailChangeForm__Field}>
              <Label>新しいメールアドレス</Label>
              <div className={styles.EmailChangeForm__InputWrapper}>
                <Input
                  id="email"
                  type="email"
                  placeholder="新しいメールアドレスを入力"
                  disabled={inputDisabled}
                  {...registerWithInnerRef('email')}
                />
              </div>
              {touchedFields.email && errorMessages.email && (
                <ErrorList errors={errorMessages.email} />
              )}
              {error && !dirtyFields.email && (
                <ErrorList errors={[error.message]} />
              )}
            </Field>
          </FormGroup>
        </MyPagePanel>
      </Paper>
      <div className={styles.EmailChangeForm__Buttons}>
        <Button
          type="submit"
          color="primary"
          size="m"
          disabled={buttonDisabled}
        >
          認証メールを送信
        </Button>
        <Button type="button" color="link" size="m" onClick={handleCancel}>
          キャンセル
        </Button>
      </div>
    </form>
  );
};

export default EmailChangeForm;
